import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faCheck,
  faFont,
  faTextHeight,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./CustomDropdown.module.css";
import { useLanguage } from "../../context/LanguageContext";

interface Option {
  label: string;
  value: string;
}

interface CustomDropdownProps {
  label: string;
  options: (string | Option)[];
  value: string | string[];
  multiple?: boolean;
  onChange: (value: any) => void;
  error?: string;
  touched?: boolean;
  icon?: IconDefinition;
  disabled?: boolean;
}

export default function CustomDropdown({
  label,
  options,
  value,
  multiple = false,
  onChange,
  error,
  touched,
  icon,
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalized = options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt,
  );

  const handleSelect = (opt: Option) => {
    if (multiple) {
      const current = Array.isArray(value) ? [...value] : [];
      const idx = current.indexOf(opt.value);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(opt.value);
      onChange(current);
    } else {
      onChange(opt.value);
      setIsOpen(false);
    }
  };

  const displayValue = multiple
    ? Array.isArray(value) && value.length > 0
      ? normalized
          .filter((opt) => value.includes(opt.value))
          .map((opt) => opt.label)
          .join(", ")
      : t("selectPlaceholder")
    : normalized.find((opt) => opt.value === value)?.label ||
      t("selectPlaceholder");

  const isFontDropdown = /font\s*family/i.test(label);
  const isFontSizeDropdown = /font\s*size/i.test(label);

  const sizeMap: Record<string, string> = {
    Small: "12px",
    Medium: "16px",
    Large: "20px",
  };

  return (
    <div className={styles.fieldWrapper} ref={dropdownRef}>
      <label className={styles.label}>{label}</label>

      <button
        type="button"
        className={styles.dropdownButton}
        onClick={() => setIsOpen(!isOpen)}
      >
        {icon && <FontAwesomeIcon icon={icon} className={styles.icon} />}
        <span className={styles.text}>{displayValue}</span>
        <FontAwesomeIcon
          icon={isOpen ? faChevronUp : faChevronDown}
          className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ""}`}
        />
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          {normalized.map((opt) => {
            const selected = multiple
              ? Array.isArray(value) && value.includes(opt.value)
              : value === opt.value;

            return (
              <div
                key={opt.value}
                className={`${styles.dropdownItem} ${selected ? styles.selected : ""}`}
                onClick={() => handleSelect(opt)}
              >
                <div className={styles.fontPreview}>
                  {isFontDropdown && (
                    <FontAwesomeIcon
                      icon={faFont}
                      className={styles.fontIcon}
                    />
                  )}
                  {isFontSizeDropdown && (
                    <FontAwesomeIcon
                      icon={faTextHeight}
                      className={styles.fontIcon}
                    />
                  )}

                  <span
                    style={
                      isFontDropdown
                        ? { fontFamily: opt.value }
                        : isFontSizeDropdown
                          ? { fontSize: sizeMap[opt.label] || "16px" }
                          : {}
                    }
                    className={styles.fontName}
                  >
                    {opt.label}
                  </span>
                </div>

                {selected && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={styles.checkmark}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
